import { useEffect, useRef, useState } from "react";
import {
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import Dashboard from "./Dashboard";
import logoMarkMax from "../assets/logo-mark-promax.png";
import SnowBackground from "../components/SnowBackground";

const provider = new GoogleAuthProvider();

// Skeleton shown while Firebase resolves auth state OR after login click
function DashboardSkeleton({ message }) {
  return (
    <div className="ggd-skeleton-shell">
      <div className="ggd-skeleton-sidebar">
        <div className="ggd-sk ggd-sk--logo" />
        <div className="ggd-sk ggd-sk--nav" />
        <div className="ggd-sk ggd-sk--nav" />
        <div className="ggd-sk ggd-sk--nav" />
        <div className="ggd-sk ggd-sk--nav" />
      </div>
      <div className="ggd-skeleton-main">
        <div className="ggd-skeleton-topbar" />
        <div className="ggd-skeleton-content">
          <div className="ggd-skeleton-row">
            <div className="ggd-sk ggd-sk--card-tall" />
            <div className="ggd-sk ggd-sk--card-tall" />
          </div>
          <div className="ggd-skeleton-row ggd-skeleton-row--3">
            <div className="ggd-sk ggd-sk--card" />
            <div className="ggd-sk ggd-sk--card" />
            <div className="ggd-sk ggd-sk--card" />
          </div>
          <div className="ggd-sk ggd-sk--card-wide" />
        </div>
      </div>

      {/* Optional pill message shown only during login (not on page load) */}
      {message && (
        <div className="ggd-skeleton-msg">{message}</div>
      )}
    </div>
  );
}

export default function Login() {
  const [userData, setUserData] = useState(null);
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const snowRef = useRef(null);
  const [error, setError] = useState("");

  const loadUserDashboard = async (firebaseUser) => {
    const userEmail = firebaseUser.email.toLowerCase();

    const userRef = doc(db, "users", userEmail);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      setError("Access denied. Your account is not registered for this dashboard.");
      await signOut(auth);
      setUserData(null);
      setDirectors([]);
      return;
    }

    const loggedInUserData = userSnap.data();

    const directorsQuery = query(
      collection(db, "directors"),
      where("sponsorUsanaId", "==", loggedInUserData.usanaId)
    );

    const directorsSnapshot = await getDocs(directorsQuery);

    const directorList = directorsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    setUserData(loggedInUserData);
    setDirectors(directorList);
    setError("");
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          await loadUserDashboard(firebaseUser);
        } else {
          setUserData(null);
          setDirectors([]);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard. Please try again.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    // A soft gust sweeps the snow while the Google window opens
    snowRef.current?.gust();
    try {
      setPopupOpen(true);
      setError("");
      const result = await signInWithPopup(auth, provider);
      // Skeleton only appears after the Google popup closes (while Firestore loads)
      setLoginLoading(true);
      await loadUserDashboard(result.user);
    } catch (err) {
      console.error(err);
      setError("Login failed. Please try again.");
    } finally {
      setPopupOpen(false);
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUserData(null);
    setDirectors([]);
    setError("");
  };

  // ── CHANGED: now also shows skeleton after login button click ──
  // loading     = page load (Firebase checking existing session) → no message
  // loginLoading = after Google popup closes, fetching Firestore → shows pill
  if (loading || loginLoading) {
    return (
      <DashboardSkeleton
        message={loginLoading ? "Signing you in…" : null}
      />
    );
  }

  // Authenticated — show dashboard
  if (userData) {
    return (
      <Dashboard
        userData={userData}
        directors={directors}
        onLogout={handleLogout}
      />
    );
  }

  // Not authenticated — show login page
  return (
    <main className="login-page login-page--aurora">

      {/* ── Aurora gradient layers ── */}
      <div className="login-aurora-bg" aria-hidden="true">
        <div className="login-aurora login-aurora--1" />
        <div className="login-aurora login-aurora--2" />
        <div className="login-aurora login-aurora--3" />
      </div>

      {/* ── Twinkling stars ── */}
      <div className="login-stars" aria-hidden="true">
        {[
          { top: "12%", left: "8%",  size: 2,   delay: 0   },
          { top: "18%", left: "22%", size: 1,   delay: 1.2 },
          { top: "8%",  left: "38%", size: 2.5, delay: 0.5 },
          { top: "22%", left: "52%", size: 1.5, delay: 2   },
          { top: "10%", left: "68%", size: 2,   delay: 0.8 },
          { top: "16%", left: "82%", size: 1,   delay: 1.5 },
          { top: "28%", left: "12%", size: 1.5, delay: 2.3 },
          { top: "32%", left: "92%", size: 2,   delay: 0.3 },
          { top: "42%", left: "5%",  size: 1,   delay: 1.8 },
          { top: "48%", left: "95%", size: 1.5, delay: 0.9 },
          { top: "62%", left: "3%",  size: 2,   delay: 2.5 },
          { top: "72%", left: "88%", size: 1,   delay: 0.4 },
          { top: "80%", left: "15%", size: 1.5, delay: 1.6 },
          { top: "88%", left: "72%", size: 2,   delay: 2.1 },
          { top: "92%", left: "45%", size: 1,   delay: 0.7 },
        ].map((s, i) => (
          <span
            key={i}
            className="login-star"
            style={{
              top: s.top,
              left: s.left,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
      </div>

      {/* ── 3D watercolour snowfall (Three.js, transparent over the aurora) ── */}
      <SnowBackground ref={snowRef} />

      {/* ── Soft glow that lifts the card off the snow ── */}
      <div className="login-card-glow" aria-hidden="true" />

      {/* ── Frosted glass login card ── */}
      <section className="login-card login-card--glass">
        <div className="login-brand">
          <img
            src={logoMarkMax}
            alt="USANA Go Getter Discovery"
            className="login-logo-img"
          />
        </div>

        <div className="login-hero">
          <p className="login-teaser">Your journey to Hokkaido starts here ✨</p>
          <p className="login-subtitle">
            Track your qualification points, CP growth, and director progress — all in one place.
          </p>
        </div>

        {error && (
          <div className="login-error" role="alert">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M7.5 4.5v4M7.5 10.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <button
          className="google-btn"
          onClick={handleLogin}
          disabled={loginLoading || popupOpen}
        >
          {loginLoading || popupOpen ? (
            <span className="btn-spinner" />
          ) : (
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google"
              width="18"
              height="18"
            />
          )}
          <span>{popupOpen ? "Opening Google…" : loginLoading ? "Signing in…" : "Sign in with Google"}</span>
        </button>

        <p className="login-note">
          Access is restricted to registered Gmail accounts only.
        </p>
      </section>
    </main>
  );
}